import React, { PureComponent, useState, useRef, useEffect, useCallback, useContext, useMemo, forwardRef } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetTask, makeGetFilteredTask } from '../selectors/tasks';
import { makeGetMilestone } from '../selectors/milestones';
import { makeGetTaskNotifications } from '../selectors/notifications';
import { makeGetProject } from '../selectors/projects';

import { fetchTask, updateTask, assignTask, unassignTask } from '../actions/tasks';
import { showModal } from '../actions/modal';

import { isTempId } from '../utils/helpers';
import { canChangeDueDate } from '../utils/task';
import { trackEvent } from '../services/events-tracker-service';

import { useProjectHasAutoMilestones } from 'shared-hooks/project';
import { useIsDependant } from 'shared-hooks/task';

import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';

import TaskCompleteToggle from 'components/TaskCompleteToggle';
import MultiLineInput from 'components/MultiLineInput';
import TaskNiceId from 'components/TaskNiceId';
import CanUser from './can-user';
import TaskActions from 'components/TaskActions';
import MilestoneName from './milestone-name';
import ProjectName from './project-name';
import UsersList from './users-list';
import LabelsList from './labels-list';
import Tooltip from './tooltip';
import NiftyMarkdown from './nifty-markdown';
import TaskDueDate from 'components/TaskDueDate';
import TaskAssignees from 'components/TaskAssignees';
import CircleToggle from 'components/CircleToggle';
import FeatureAvailable from 'components/feature-available';
import CustomFields from 'components/custom-fields';

import IconRoadmap from '../icons/icon-roadmap';
import IconSubtask from '../icons/icon-subtask';
import IconFile from '../icons/icon-file';
import IconDiscussionAlt from '../icons/icon-discussion-alt';
import IconEye from '../icons/icon-eye';
import IconCalendarSimple from '../icons/icon-calendar-simple';
import IconRepeat from '../icons/icon-repeat';
import IconProject from '../icons/icon-project';
import IconClose from '../icons/icon-close';

const TaskInner = ({ id, task, selected, isTemp, isPersonal, showActions, notificationsCount, projectDisabledModules, milestone, dependantOn, labelsFetched, simpleTask, publicPage, showProject, meetingId, withAddOverlay, hiddenKanbanFields = [], onClick, onRemoveFromMetting, onCompleteToggle, onSelect, onAssigneeRemove, onAssigneeAdd, onResourceLoad }) => {
    projectDisabledModules = (!projectDisabledModules ? [] : projectDisabledModules);
    const [editing, setEditing] = useState(false);
    const nameInputCtrl = useRef();
    const rolesAndPermissions = useContext(RolesAndPermissionsContext);
    const hasAutoMilestones = useProjectHasAutoMilestones(task.project);
    const { t } = useTranslation('task');

    const withAddToMeeting = useMemo(() => {
        return withAddOverlay && meetingId;
    }, [withAddOverlay, meetingId]);

    const addedToMeeting = useMemo(() => {
        return (task?.meeting_ids || []).includes(meetingId);
    }, [task?.meeting_ids, meetingId]);

    const handleEditNameClick = useCallback(() => {
        setEditing(true);

        requestAnimationFrame(() => {
            nameInputCtrl.current && nameInputCtrl.current.focus(true);
        })
    }, [nameInputCtrl, setEditing]);

    const handleNameBlur = useCallback(nameValue => {
        setEditing(false);

        if (!nameValue) {
            nameInputCtrl.current && nameInputCtrl.current.reset(true);
            return;
        }

        updateTask(id, { name: nameValue });
    }, [id, nameInputCtrl, setEditing]);

    const handleDueDateChange = useCallback(({ nextDate, nextStartDate }) => {
        if (!hasAutoMilestones && nextDate && !canChangeDueDate(nextDate, milestone, false)) {
            showModal('EXTEND_MILESTONES_MODAL', {
                milestones: [milestone],
                nextDate,
                onSuccess: () => {
                    updateTask(id, {
                        due_date: nextDate,
                        start_date: nextStartDate
                    });
                }
            });

            return;
        };

        updateTask(id, {
            due_date: nextDate,
            start_date: nextStartDate
        });
    }, [id, milestone, hasAutoMilestones]);

    return (
        <React.Fragment>
            <div className={'task-inner' + (withAddToMeeting ? ' with-add-to-meeting no-hover' : '') + (editing ? ' editing' : '')} data-notification={notificationsCount}>
                <div className={'task-overlay' + (withAddToMeeting ? ' actionable' : '') + (withAddToMeeting && addedToMeeting ? ' always-visible' : '')} onClick={onClick} />

                {
                    showActions ? (
                        <div className="task-actions-wrapper">
                            {
                                !withAddToMeeting ? (
                                    <TaskActions id={id} onEditNameClick={handleEditNameClick} />
                                ) : null
                            }

                            <CanUser type="editTask" customList={task.created_by}>
                                {
                                    !withAddToMeeting && onSelect ? (
                                        <Tooltip placement="top" tooltip={ t('Select multiple tasks') }>
                                            <div className="task-select">
                                                <CircleToggle
                                                    forceUseProp={true}
                                                    checked={selected}
                                                    onChange={onSelect}
                                                />
                                            </div>
                                        </Tooltip>
                                    ) : null
                                }
                            </CanUser>

                            <CanUser type="editTask" customList={task.created_by}>
                                {
                                    addedToMeeting ? (
                                        <Tooltip tooltip="Remove from the meeting">
                                            <span className="task-actions-trigger task-remove-from-meeting" onClick={onRemoveFromMetting}>
                                                <IconClose className="icon-only" />
                                            </span>
                                        </Tooltip>
                                    ) : null
                                }
                            </CanUser>
                        </div>
                    ) : null
                }

                <div className="task-top">
                    {
                        !isPersonal && !hiddenKanbanFields?.includes('nice_id') && task.nice_id ? (
                            <span className="task-id-holder">
                                <TaskNiceId niceId={task.nice_id} />
                            </span>
                        ) : null
                    }

                    {
                        !hiddenKanbanFields?.includes('labels') && labelsFetched && task?.labels?.length ? (
                            <div className="task-labels">
                                <LabelsList labels={task.labels} inverted={true} />
                            </div>
                        ) : null
                    }
                </div>

                <div className="task-name">
                    <CanUser type="completeTask" customList={task.created_by}>
                        <div className="task-complete-toggle-holder">
                            <TaskCompleteToggle
                                id={id}
                                checkSubtasks={true}
                                dependantOn={dependantOn}
                                completed={task.completed}
                                disabled={isTemp}
                                projectId={task.project}
                                onChange={onCompleteToggle}
                            />
                        </div>
                    </CanUser>

                    <MultiLineInput
                        className="task-name-text"
                        placeholder={`${t('Add task name')}…`}
                        canEdit={editing}
                        value={task.name}
                        provideController={nameInputCtrl}
                        onBlur={handleNameBlur}
                    />
                </div>

                {
                    !hiddenKanbanFields?.includes('banner') && !simpleTask && task.banner ? (
                        <div className="task-banner">
                            <img width="250px" height="180px" src={task.banner.replace('w-360,h-360', 'w-500,h-360')} alt={t('Task banner')} />
                        </div>
                    ) : null
                }

                {
                    !hiddenKanbanFields?.includes('description') && task.description ? (
                        <div className="task-description">
                            <NiftyMarkdown unstyled={true}>{task.description}</NiftyMarkdown>
                        </div>
                    ) : null
                }

                {
                    !hiddenKanbanFields?.includes('custom_fields') ? (
                        <FeatureAvailable type="customFields">
                            <CustomFields
                                projectId={task.project}
                                type="row-single"
                                association="task"
                                associationId={id}
                                values={task.fields}
                                withEdit={false}
                                showEmpty={false}
                            />
                        </FeatureAvailable>
                    ) : null
                }

                <div className="task-foot">
                    <div className="meta">
                        {
                            !publicPage && showProject ? (
                                <div className="meta-item">
                                    <span className="meta-icon">
                                        <IconProject />
                                    </span>

                                    <span className="meta-text">
                                        <ProjectName id={task.project} onLoad={onResourceLoad} />
                                    </span>
                                </div>
                            ) : !publicPage && task.milestone && !projectDisabledModules.includes('milestones') ? (
                                <div className="meta-item">
                                    <span className="meta-icon">
                                        <IconRoadmap />
                                    </span>

                                    <span className="meta-text">
                                        <MilestoneName id={task.milestone} onLoad={onResourceLoad} />
                                    </span>
                                </div>
                            ) : null
                        }

                        {
                            task.due_date ? (
                                <TaskDueDate id={task.id} withIcon={true} onChange={handleDueDateChange} />
                            ) : null
                        }

                        {
                            task.total_subtasks ? (
                                <div className={'meta-item' + (task.total_subtasks === task.completed_subtasks ? ' active' : '')}>
                                    <span className="meta-icon">
                                        <IconSubtask />
                                    </span>

                                    <span className="meta-text">
                                        { task.completed_subtasks || 0 }/{ task.total_subtasks || 0 }
                                    </span>
                                </div>
                            ) : null
                        }

                        {
                            task.comments ?
                                <div className="meta-item">
                                    <span className="meta-icon" style={{marginRight: '2px'}}>
                                        <IconDiscussionAlt />
                                    </span>

                                    <span className="meta-text">
                                        { task.comments }
                                    </span>
                                </div>
                                : null
                        }

                        {
                            task.attachments && task.attachments.length ?
                                <div className="meta-item">
                                    <span className="meta-icon">
                                        <IconFile />
                                    </span>

                                    <span className="meta-text">
                                        { task.attachments.length }
                                    </span>
                                </div>
                                : null
                        }

                        {
                            task.hidden?.length ?
                                <Tooltip tooltip={ t('Task hidden from', { roles: rolesAndPermissions?.getRolesListString(task.hidden, true) }) }>
                                    <div className="meta-item with-tooltip">
                                        <span className="meta-icon meta-icon-single">
                                            <IconEye />
                                        </span>
                                    </div>
                                </Tooltip>
                                : null
                        }

                        {
                            !task.archived && task.rule ?
                                <Tooltip tooltip={ t('This task is recurring') }>
                                    <div className="meta-item with-tooltip">
                                        <span className="meta-icon meta-icon-single">
                                            <IconRepeat size="12px" />
                                        </span>
                                    </div>
                                </Tooltip>
                                : null
                        }
                    </div>

                    <div className="meta-item">
                        <TaskAssignees id={id} avatarSize="xsmall" maxUsersCount={2} hideIfEmpty={true} />
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
};

const Task = forwardRef(({ id, task, isTemp, selected, style, showActions = true, notificationsCount, projectDisabledModules, milestone, simpleTask, labelsFetched, publicPage, meetingId, withAddOverlay, showProject, hiddenKanbanFields, isDragging, current, isOver, draggedTask, filtered, index, onDragStart, onDragEnd, onSelect, onClick, onMeasureChange }, ref) => {
    const [, dependantOn] = useIsDependant(id);

    const element = useRef(null);

    const className = useMemo(() => {
        const className = ['task'];
        if (isDragging) className.push('is-dragging');
        if (current) className.push('active');
        if (isOver) className.push('is-over');
        if (task?.archived) className.push('archived');
        if (selected) className.push('selected');
        if (task?.completed) className.push('completed');
        if (draggedTask === id) className.push('dragged-task');
        if (simpleTask) className.push('simple-task');
        if (dependantOn) className.push('dependant');
        if (onDragStart) className.push('ignore-html5-dnd');

        return className.join(' ');
    }, [isDragging, current, isOver, task?.archived, selected, task?.completed, draggedTask, id, simpleTask, dependantOn, onDragStart]);


    const doUpdateTask = useCallback((taskProps, preventSave = false) => {
        return updateTask(id, taskProps, preventSave);
    }, [id]);

    const isPersonal = useMemo(() => {
        if (!task) {
            return false;
        }

        return !task.task && !task.task_group;
    }, [task]);

    const handleTaskClick = useCallback(event => {
        if (!id || isTemp) {
            return;
        }

        if (meetingId && withAddOverlay) {
            doUpdateTask({
                meeting_ids: [...(task.meeting_ids || []), meetingId]
            });
            return;
        }

        const { metaKey, shiftKey } = event;

        if ((metaKey || shiftKey) && onSelect) {
            onSelect(task, {
                metaKey,
                shiftKey,
                select: !selected
            });

            return;
        }


        onClick && onClick(isPersonal ? id : (task.nice_id || id));
    }, [isPersonal, id, isTemp, meetingId, withAddOverlay, task, selected, doUpdateTask, onSelect, onClick]);

    const handleRemoveFromMettingClick = useCallback(() => {
        doUpdateTask({
            meeting_ids: (task?.meeting_ids || []).filter(mId => mId !== meetingId)
        });
    }, [task?.meeting_ids, meetingId, doUpdateTask]);

    const handleAssigneeRemove = useCallback(userId => {
        unassignTask(id, [userId]);
    }, [id]);

    const handleAssigneeAdd = useCallback(user => {
        assignTask(id, [user.id || user]);
    }, [id]);

    const handleTaskCompleteToggle = useCallback(async (completed, taskGroupId) => {
        if (isTemp) {
            return;
        }

        const nextProps = {
            completed,    
            task_group: taskGroupId    
        }    

        if (completed) {    
            nextProps.completed_on = new Date();    
        }    

        try {    
            await doUpdateTask(nextProps);    

            if (completed) {    
                trackEvent('TASK.COMPLETE');    
            }
        } catch (e) {
            // es lint
        }
    }, [isTemp, doUpdateTask]);

    const handleSelectToggle = useCallback(select => {
        if (isTemp) {
            return;
        }

        onSelect(task, { select });
    }, [isTemp, task, onSelect]);

    const handleDragStart = useCallback(() => {
        onDragStart(id);
    }, [id, onDragStart]);

    const handleDragEnd = useCallback(() => {
        onDragEnd(id);
    }, [id, onDragEnd]);

    const handleResourceLoad = useCallback(() => {
        onMeasureChange?.(element.current);
    }, [onMeasureChange]);

    const setRef = useCallback(el => {
        element.current = el;
        ref?.(el);
    }, [ref]);

    useEffect(() => {
        if (id && !Boolean(task) && !filtered) {
            fetchTask(id);
        }
    }, [id, Boolean(task), filtered]);

    useEffect(() => {
        ref?.(element.current);
    }, [task, hiddenKanbanFields]);

    if (!task) {
        return null;
    }

    return (
        <div
            className={className}
            draggable={onDragStart && 'true'}
            onDragStart={onDragStart && handleDragStart}
            onDragEnd={onDragEnd && handleDragEnd}
            style={style}
            data-index={index}
            ref={setRef}
        >
            <TaskInner
                id={task.id}
                task={task}
                selected={selected}
                isTemp={isTemp}
                isPersonal={isPersonal}
                showActions={showActions}
                notificationsCount={notificationsCount}
                projectDisabledModules={projectDisabledModules}
                milestone={milestone}
                dependantOn={dependantOn}
                simpleTask={simpleTask}
                labelsFetched={labelsFetched}
                publicPage={publicPage}
                meetingId={meetingId}
                withAddOverlay={withAddOverlay}
                showProject={showProject}
                hiddenKanbanFields={hiddenKanbanFields}
                onClick={handleTaskClick}
                onRemoveFromMetting={handleRemoveFromMettingClick}
                onCompleteToggle={handleTaskCompleteToggle}
                onSelect={onSelect && handleSelectToggle}
                onAssigneeRemove={handleAssigneeRemove}
                onAssigneeAdd={handleAssigneeAdd}
                onResourceLoad={handleResourceLoad}
            />
        </div>
    );
});

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();
    const getTaskNotifications = makeGetTaskNotifications();
    const getFilteredTask = makeGetFilteredTask();
    const getProject = makeGetProject();

    return ({ projects, tasks, notifications, milestones }, ownProps) => {
        const task = ownProps.id && getTask(tasks.items, ownProps.id);
        const filteredTask = task && getFilteredTask(task, tasks.filter);
        const project = task && getProject(projects.items, task.project);

        return {
            task: filteredTask,
            milestone: project && !(project.disabled_modules || []).includes('milestones') && task.milestone && getMilestone(milestones.items, task.milestone),
            isTemp: isTempId(ownProps.id),
            projectDisabledModules: project?.disabled_modules,
            filtered: !filteredTask && task,
            notificationsCount: (ownProps.id ? getTaskNotifications(notifications.items, ownProps.id) : []).length
        }
    }
}

export default connect(makeMapStateToProps, null, null, { forwardRef: true })(Task);
